import { makeAutoObservable, runInAction } from "mobx";
import { RootStore } from "../shared/stores/RootStore";
import { City } from "../survey/models/City";
import { Feature, MultiPolygon, Polygon } from "geojson";
import { OsmData, OsmWay } from "../map/models/OsmData";
import { toast } from "../shared/views/CustomToaster";
import { CityApi } from "../map/services/CityApi";
import { AdminOsmApi } from "../map/services/AdminOsmApi";
import { JobStateApi } from "../shared/services/JobStateApi";
import { JobStatus } from "../shared/models/JobState";
import { AdminCityApi } from "../admin/services/AdminCityApi";
import { StreetViewMapSource } from "../map/sources/StreetViewMapSource";
import { addCustomIcon } from "../map/utils/map-utils";
import { TranslateOsmTag } from "../map/models/TranslateOsmTag";
import { cameraSvg } from "../shared/assets";

type AllOsmTags = Record<string, string[]>;
type Tag = {
  key: string;
  value: string;
};

export class OsmToolsStore {
  rootStore;

  map?: mapboxgl.Map;
  city?: City;
  polygon?: Feature<Polygon | MultiPolygon>;
  data?: OsmData;
  allTags?: AllOsmTags;
  selectedWay?: OsmWay;
  highlightedTag?: Tag;
  streetViewMapSource?: StreetViewMapSource;
  querying = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  resetState() {
    runInAction(() => {
      this.city = undefined;
      this.polygon = undefined;
      this.data = undefined;
      this.map = undefined;
      this.allTags = undefined;
      this.selectedWay = undefined;
      this.highlightedTag = undefined;
      this.streetViewMapSource = undefined;
      this.querying = false;
    });
  }

  initStreetViewSource(map: mapboxgl.Map) {
    this.streetViewMapSource?.clear();
    this.streetViewMapSource = new StreetViewMapSource(map);

    addCustomIcon(map, "camera-icon", cameraSvg, 30);
  }

  registerMap(map: mapboxgl.Map) {
    runInAction(() => {
      this.map = map;
    });
  }

  selectWay(wayId: number | undefined) {
    runInAction(() => {
      this.selectedWay = this.data?.ways?.find((w) => w.id === wayId);
    });
  }

  highlightTag(key: string, value: string) {
    runInAction(() => {
      this.highlightedTag = { key, value };
    });
  }

  async fetchCity(cityId: string) {
    await CityApi.get(cityId!).then(({ data }) => {
      runInAction(() => {
        this.city = data;
      });
    });
  }

  async fetchData(cityId: string, cancelIf?: () => boolean) {
    await CityApi.getOsmData(cityId!).then(({ data }) => {
      runInAction(() => {
        if (cancelIf?.()) return;
        this.data = data;
        this.allTags = this.processAllTags(data);
      });
    });
  }

  async deleteData(cityId: string) {
    await AdminCityApi.deleteOsmData(cityId!).then(() => {
      runInAction(() => {
        this.data = undefined;
        this.allTags = undefined;
      });
    });
  }

  async translateTag(body: TranslateOsmTag) {
    await AdminOsmApi.translateTag(body).then(() => {
      this.fetchData(body.cityId);
    });
  }

  processAllTags(data: OsmData): AllOsmTags {
    const allTags: AllOsmTags = {};

    data.ways.forEach(({ tags }) => {
      Object.entries(tags).forEach(([key, value]) => {
        const tagValues = new Set(allTags[key] ?? []);

        tagValues.add(value);

        allTags[key] = [...tagValues];
      });
    });

    return allTags;
  }

  async queryPolygon(cityId: string, polygon: number[][]) {
    this.setQuerying(true);
    await AdminOsmApi.queryPolygon({
      cityId: cityId,
      polygon: polygon,
    }).then(async ({ data: jobStateId }) => {
      await JobStateApi.poll(jobStateId, 2000, (jobState) => {
        if (jobState.status === JobStatus.FAILED) {
          toast("error", "Query failed");
        } else if (jobState.status === JobStatus.FINISHED) {
          toast("success", "Query done");

          this.fetchData(cityId!);
        }
      });

      this.setQuerying(false);
    });
  }

  setQuerying(value: boolean) {
    runInAction(() => {
      this.querying = value;
    });
  }

  searchPolygon() {
    const { name, country } = this.city!;

    AdminOsmApi.searchPolygon(name, country).then(({ data }) => {
      const cityPolygon = data.features[0];
      if (cityPolygon) {
        runInAction(() => {
          this.polygon = cityPolygon as Feature<Polygon>;
        });
      } else {
        toast("error", "Unable to find city polygon");
      }
    });
  }
}
